import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import type {
  SchemaConnection,
  SchemaData,
  SchemaModesSvg,
  SchemaStation,
  SchemaText,
  SchemaTransition,
} from '../schema/types'
import type { MapVariant, Theme } from '../geometry/types'

/**
 * Imperative handle — same `apply()` shape as {@link StationLabelsOverlay}
 * so the canvas's rAF tick can keep the SVG overlay locked to the WebGL
 * viewport without re-rendering React on every pan/zoom frame.
 */
export interface RouteHighlightOverlayHandle {
  apply(zoom: number, offsetX: number, offsetY: number, dpr: number): void
}

export interface RouteHighlightOverlayProps {
  /**
   * Element ids (`connection-…`, `transition-…`, `station-…`) that the
   * route covers. The overlay renders the matching `svgPath` strings from
   * the schema at full opacity above the dim layer.
   *
   * Empty set ⇒ overlay renders nothing (still mounted so the imperative
   * `apply()` ref stays stable).
   */
  readonly highlightedIds: ReadonlySet<string>
  readonly schema: SchemaData | null
  readonly theme: Theme
  /**
   * The map draws the late-night frame (TASK-1136): the highlight takes each
   * element's night-frame markup (`modesSvg.night`), so the route sits on
   * the night map's lines; an element the frame does not draw falls back to
   * its dark markup.
   */
  readonly nightFrame?: boolean
}

/**
 * Pick the appropriate per-variant SVG path string. The night frame falls
 * back to the dark markup, any variant then to the base `svgPath`.
 */
function pickSvg(
  base: string | undefined,
  modes: SchemaModesSvg | undefined,
  variant: MapVariant,
): string | null {
  const night = variant === 'night' ? modes?.night : undefined
  if (typeof night === 'string' && night.length > 0) return night
  const themed = modes?.[variant === 'night' ? 'dark' : variant]
  if (typeof themed === 'string' && themed.length > 0) return themed
  if (typeof base === 'string' && base.length > 0) return base
  return null
}

/**
 * The schema occasionally serialises SVG fragments with the explicit
 * `ns0:` XML namespace prefix (e.g. `<ns0:g xmlns:ns0="…">`). Browsers
 * inject those literally into HTML5 inline-SVG via innerHTML and the
 * unrecognised prefix yields invisible elements. Strip it before
 * injection so the geometry actually renders.
 */
function stripXmlNamespace(svg: string): string {
  return svg.replace(/<\/?ns0:/g, (m) => m.replace('ns0:', '')).replace(/\s+xmlns:ns0="[^"]*"/g, '')
}

/**
 * Concatenate the SVG fragments for a given record subset into one HTML
 * string. Each schema record carries its `<path …/>` already pre-formed,
 * so we just join. Returned via `dangerouslySetInnerHTML` so the browser
 * parses paths in a single batch instead of mounting one React node per
 * path (the schema can carry hundreds of texts in scope at once).
 */
function buildHtml<T extends { svgPath?: string; modesSvg?: SchemaModesSvg }>(
  records: readonly T[],
  variant: MapVariant,
): string {
  const out: string[] = []
  for (const r of records) {
    const svg = pickSvg(r.svgPath, r.modesSvg, variant)
    if (svg !== null) out.push(stripXmlNamespace(svg))
  }
  return out.join('')
}

/**
 * Renders the highlighted subset of map geometry — connections and their
 * direction arrows, transitions, station glyphs, and station texts — at full opacity above
 * the dim layer. Drives the "lift through dim" effect that makes the
 * selected route variant pop out without requiring a second canvas.
 *
 * Schema is the source of truth for SVG paths; `skipInitialDraw`
 * transitions and texts (legacy optimisations for cold-start performance)
 * are NOT in the cold-start MMTG `elements`, but their `svgPath` strings
 * are still in the schema, so this overlay renders them correctly.
 */
export const RouteHighlightOverlay = forwardRef<
  RouteHighlightOverlayHandle,
  RouteHighlightOverlayProps
>(function RouteHighlightOverlay({ highlightedIds, schema, theme, nightFrame = false }, handleRef) {
  const variant: MapVariant = nightFrame ? 'night' : theme
  const svgRef = useRef<SVGSVGElement>(null)

  useImperativeHandle(
    handleRef,
    () => ({
      apply(zoom: number, offsetX: number, offsetY: number, dpr: number): void {
        const el = svgRef.current
        if (el === null || zoom === 0) return
        const zoomCss = zoom / dpr
        const tx = -offsetX * zoomCss
        const ty = -offsetY * zoomCss
        el.style.transform = `translate(${tx}px, ${ty}px) scale(${zoomCss})`
      },
    }),
    [],
  )

  const html = useMemo(() => {
    if (schema === null || highlightedIds.size === 0) return ''
    const connections: SchemaConnection[] = []
    const transitions: SchemaTransition[] = []
    const stations: SchemaStation[] = []
    for (const c of schema.connections) {
      if (highlightedIds.has(c.id)) connections.push(c)
    }
    // Direction arrows of the highlighted one-way connections — drawn after
    // every connection so they sit over the lines, like the MMTG does.
    const arrows = schema.additional.filter(
      (a) => a.connectionId !== undefined && highlightedIds.has(a.connectionId),
    )
    for (const t of schema.transitions ?? []) {
      if (highlightedIds.has(t.id)) transitions.push(t)
    }
    for (const s of schema.stations) {
      if (highlightedIds.has(s.id)) stations.push(s)
    }
    // Texts: schema.texts have `stationId` — render text whose station
    // belongs to the highlighted set. Critical: the user must see the
    // station name labels for the route stops above the dim layer.
    const texts: SchemaText[] = []
    for (const t of schema.texts ?? []) {
      if (t.stationId !== undefined && highlightedIds.has(t.stationId)) {
        texts.push(t)
      }
    }
    // Transitions draw in their own colours: v2 markup carries each theme's
    // (light: white pills, #D9D9D9 borders and bridges; dark: #1C1C1C,
    // #A7A7A7). A v1-era `rect{… !important}` override repainted the 6 px
    // grey bridge white with a hairline border (owner 2026-10-19).
    const transitionHtml =
      transitions.length === 0
        ? ''
        : `<g class="r-transition">${buildHtml(transitions, variant)}</g>`
    return (
      buildHtml(connections, variant) +
      buildHtml(arrows, variant) +
      buildHtml(stations, variant) +
      // Transitions render AFTER stations so the transfer glyph sits ON
      // TOP of the station ovals it bridges (consistent with iOS/Android
      // layering). Texts last so labels are above everything.
      transitionHtml +
      buildHtml(texts, variant)
    )
  }, [highlightedIds, schema, variant])

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        // Match StationLabelsOverlay: clip the 4096-wide inner SVG so it
        // never escapes the canvas viewport and pushes layout sideways.
        // Without this, the absolutely-positioned <svg> stretches the
        // parent's overflow box on mobile (Safari), shaving the right
        // edge of the map under the device chrome.
        overflow: 'hidden',
        pointerEvents: 'none',
        // Above the DOM dim layer (z=2) but below A/B bubbles (z=3+).
        zIndex: 3,
      }}
    >
      <svg
        ref={svgRef}
        viewBox="0 0 4096 4096"
        preserveAspectRatio="none"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '4096px',
          height: '4096px',
          transformOrigin: '0 0',
          willChange: 'transform',
          pointerEvents: 'none',
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  )
})
